import { ptBR } from '@/i18n/pt-BR';
import type { CaseRecordData } from '@/lib/cases';
import { Badge, LRPill, StatusPill, TableCell, TableRow } from '@/components/ui';
import { CasosRowActions } from '@/components/Casos/CasosRowActions';
import { derivedUiStatus, formatDeadline, uiStatusLabel } from '@/components/Casos/url_state';

const DATE_FMT = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
});

function formatDate(iso: string | null | undefined): string {
  if (!iso) return ptBR.casos.placeholderEmDash;
  const d = new Date(iso.includes('T') ? iso : `${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? ptBR.casos.placeholderEmDash : DATE_FMT.format(d);
}

interface CasosTableRowProps {
  caseRecord: CaseRecordData;
  alterandoPrioridade: boolean;
  baixandoRelatorio: boolean;
  onOpen: () => void;
  onPreAnalise: () => void;
  onTogglePrioridade: () => void;
  onBaixarRelatorio: () => void;
}

/**
 * Uma linha da tabela de casos (extraída da `CasosPage` na E60.F3). // ux-neutrality:allow doc-comment
 *
 * A extração veio do limite de linhas da página, mas a divisão é a que faria // ux-neutrality:allow doc-comment
 * sentido de qualquer jeito: a página cuida de carregar, filtrar e paginar; a // ux-neutrality:allow doc-comment
 * linha cuida de exibir um caso. // ux-neutrality:allow doc-comment
 */
export function CasosTableRow({
  caseRecord: c,
  alterandoPrioridade,
  baixandoRelatorio,
  onOpen,
  onPreAnalise,
  onTogglePrioridade,
  onBaixarRelatorio,
}: CasosTableRowProps): JSX.Element {
  const uiStatus = derivedUiStatus(c);
  return (
    <TableRow
      data-testid="casos-row"
      data-display-code={c.display_code}
      className="cursor-pointer"
      onClick={onOpen}
    >
      <TableCell className="font-mono text-fg">
        {c.display_code}
        {/* E60.F3: sem a marca, a lista apareceria fora da ordem de chegada
            prometida e sem explicação. */}
        {c.prioritario === true && (
          <Badge
            variant="warning"
            className="ml-2 align-middle"
            data-testid={`casos-row-prioritario-${c.display_code}`}
          >
            {ptBR.casos.table.prioritarioBadge}
          </Badge>
        )}
      </TableCell>
      <TableCell>
        <div className="text-fg">{c.title}</div>
        {c.requester !== null ? (
          <div className="text-caption text-fg-muted">{c.requester}</div>
        ) : null}
      </TableCell>
      <TableCell className="text-fg-muted">{c.especime ?? ptBR.casos.placeholderEmDash}</TableCell>
      <TableCell>
        <LRPill lr={c.latest_composite_lr ?? null} data-testid={`casos-row-lr-${c.display_code}`} />
      </TableCell>
      <TableCell>
        <StatusPill
          status={uiStatus}
          label={uiStatusLabel(uiStatus)}
          data-testid={`casos-row-status-${c.display_code}`}
        />
      </TableCell>
      <TableCell className="font-mono text-caption text-fg-muted">
        {formatDeadline(c.deadline)}
      </TableCell>
      <TableCell className="font-mono text-caption text-fg-muted">
        {formatDate(c.received_at)}
      </TableCell>
      <TableCell className="font-mono text-caption text-fg-muted">
        {formatDate(c.created_at)}
      </TableCell>
      <TableCell
        className="text-right"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <CasosRowActions
          caseRecord={c}
          onOpen={onOpen}
          onPreAnalise={onPreAnalise}
          onTogglePrioridade={onTogglePrioridade}
          onBaixarRelatorio={onBaixarRelatorio}
          alterandoPrioridade={alterandoPrioridade}
          baixandoRelatorio={baixandoRelatorio}
        />
      </TableCell>
    </TableRow>
  );
}
